/**
 * Launch an ADVICE `memo_entry` session for a received partner memo — the
 * receiver-side "Discuss this note" edge (partner-memo V1b).
 *
 * Entry-launch sequence contract: swap to the chat surface, switch to ADVICE
 * with `entryType: 'memo_entry'` + the memoId, and auto-send a visible opener.
 * The first AdviceStrategy turn preloads the memo (recipient-validated,
 * `viewedAt` stamped), pins the `<memo-reference>` card, and opens by engaging
 * the note.
 *
 * Used by `PartnerSection` (per-memo "Discuss this note" button).
 *
 * @param {Object} params
 * @param {string} params.memoId - PartnerMemo row UUID (required).
 * @param {string} [params.senderName] - Partner display name for the opener text.
 * @returns {Promise<boolean>} true if the session launched, false on a guarded bail.
 */
export async function launchMemoDiscussAdvice({ memoId, senderName = '' }) {
  if (!memoId) {
    console.warn('[memoEntryLauncher] missing memoId — cannot launch memo_entry advice');
    return false;
  }

  const orchestrator = window.app?.modules?.modeOrchestrator;
  if (!orchestrator) {
    console.error('[memoEntryLauncher] modeOrchestrator unavailable');
    window.notificationManager?.showError('Could not open the conversation. Please try again.');
    return false;
  }

  // Await the page swap and bail if PageManager superseded us — running
  // switchMode while the chat surface isn't visible would create an ADVICE
  // session the user never sees.
  const swapped = await window.app?.modules?.pageManager?.showPage('chat');
  if (swapped === false) return false;
  window.history.pushState({}, '', '/chat');

  try {
    // `force: true` is REQUIRED: the discuss click can fire while the user is
    // ALREADY in an ADVICE session (mode persists across page navigation).
    // Without force, switchMode's same-mode early-return no-ops the switch and
    // the opener below strands in the prior session. force creates a fresh
    // memo_entry session (backend closes the prior one).
    await orchestrator.switchMode('ADVICE', {
      entryType: 'memo_entry',
      memoId,
      skipWelcome: true,
      force: true
    });
    const name = typeof senderName === 'string' && senderName.trim()
      ? senderName.trim()
      : 'my partner';
    await orchestrator.handleUserMessage(`I'd like to talk about the note from ${name}.`, {
      metadata: { source: 'memo_entry', memoId }
    });
    return true;
  } catch (err) {
    console.error('[memoEntryLauncher] switchMode failed:', err);
    window.notificationManager?.showError('Could not open the conversation. Please try again.');
    return false;
  }
}
